import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { CheckSquare, ExternalLink, Linkedin, Loader2, Search, Square, UserPlus } from 'lucide-react'
import { useLinkedinSearch } from '../../../hooks/useLinkedinSearch.js'
import { useServiceCredentials } from '../../../hooks/useServiceCredentials.js'
import { useWorkerJob } from '../../../hooks/useWorkerJob.js'
import { useWorkerJobsStore } from '../../../stores/workerJobsStore.js'
import { SearchJobStatus } from '../../france-travail-candidates/components/FranceTravailSearchPanel.jsx'

/** Crédits, alignés sur la recherche B2B (miroir de LinkedinSearchController). */
const SEARCH_CREDIT_COST = 1
const PROSPECT_CREDIT_COST = 2
const DEFAULT_RESULTS = 20
const MAX_RESULTS = 50

// Dernière recherche gardée dans le navigateur : ses résultats restent à portée en revenant ici.
const LAST_JOB_KEY = 'linkedin-last-search-job'

const inputClass =
  'w-full rounded-2xl border border-ink/10 bg-ink/[0.03] px-4 py-3 text-sm text-ink outline-none transition placeholder:text-ink/30 focus:border-brand/40 focus:ring-1 focus:ring-brand/20'

const errorMessage = (error, fallback) => error?.response?.data?.error || fallback

function readLastJobId() {
  try {
    return Number(window.localStorage.getItem(LAST_JOB_KEY)) || null
  } catch {
    return null
  }
}

function saveLastJobId(id) {
  try {
    window.localStorage.setItem(LAST_JOB_KEY, String(id))
  } catch {
    // stockage indisponible (navigation privée) : on garde seulement l'affichage en cours
  }
}

/**
 * Prospection LinkedIn (page Prospection) : recherche de personnes avec le compte LinkedIn de
 * l'utilisateur, puis ajout des profils choisis aux prospects du CRM (tag LinkedIn).
 */
export default function LinkedinSearchPanel() {
  const { providers, isLoading } = useServiceCredentials()
  const linkedin = providers.find((p) => p.provider === 'linkedin')

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-ink/50">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {linkedin?.status === 'connected' ? <SearchForm /> : <ConnectFirst credential={linkedin} />}
    </div>
  )
}

function ConnectFirst({ credential }) {
  const reconnect = credential?.configured && credential.status === 'needs_reconnect'
  return (
    <div className="rounded-[14px] border border-ink/10 bg-ink/[0.03] p-6">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#0a66c2]/15 text-[#4a9be8]">
          <Linkedin className="h-5 w-5" />
        </span>
        <div>
          <h2 className="m-0 text-xl font-bold text-ink">
            {reconnect ? 'Reconnectez votre compte LinkedIn' : 'Connectez votre compte LinkedIn'}
          </h2>
          <p className="m-0 mt-1 text-sm text-ink/50">
            {reconnect && credential.statusMessage
              ? credential.statusMessage
              : 'La recherche utilise votre propre compte LinkedIn. Connectez-le une fois, puis revenez ici.'}
          </p>
        </div>
      </div>
      <Link
        to="/dashboard/compte/authentification"
        className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#0a66c2] px-4 py-2 text-sm font-semibold text-white hover:bg-[#004182]"
      >
        <Linkedin className="h-4 w-4" />
        Aller dans Compte &gt; Authentification
      </Link>
    </div>
  )
}

function SearchForm() {
  const { search } = useLinkedinSearch()
  const trackJob = useWorkerJobsStore((state) => state.track)
  const [keywords, setKeywords] = useState('')
  const [location, setLocation] = useState('')
  const [maxResults, setMaxResults] = useState(DEFAULT_RESULTS)
  const [jobId, setJobId] = useState(readLastJobId)
  const { job } = useWorkerJob(jobId)

  const clamp = (value) => Math.min(MAX_RESULTS, Math.max(1, Number(value) || DEFAULT_RESULTS))

  const handleSubmit = (event) => {
    event.preventDefault()
    if (!keywords.trim()) {
      toast.error('Indiquez un poste ou des mots-clés')
      return
    }
    search.mutate(
      { keywords: keywords.trim(), location: location.trim(), maxResults: clamp(maxResults) },
      {
        onSuccess: (created) => {
          setJobId(created.id)
          saveLastJobId(created.id)
          trackJob({ id: created.id, kind: created.kind })
          toast.success('Recherche LinkedIn lancée : vous serez prévenu à la fin.')
        },
        onError: (error) => {
          if (error?.response?.status === 402) {
            toast.error(`Crédits insuffisants (${SEARCH_CREDIT_COST} crédit par recherche)`)
          } else {
            toast.error(errorMessage(error, 'Impossible de lancer la recherche'))
          }
        },
      }
    )
  }

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-5 rounded-[14px] border border-ink/10 bg-ink/[0.03] p-6">
        <div>
          <h2 className="text-xl font-bold text-ink">Prospects LinkedIn</h2>
          <p className="mt-1 text-sm text-ink/50">
            Recherche de personnes avec votre compte LinkedIn. Vous choisissez ensuite les profils à ajouter à vos
            prospects : ils arrivent dans le CRM avec le tag LinkedIn.
          </p>
        </div>

        <div className="grid gap-5 sm:grid-cols-[2fr_1.4fr_1fr]">
          <label className="space-y-2 text-sm font-medium text-ink/70">
            <span>Poste ou mots-clés</span>
            <input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="ex. Directeur commercial BTP" className={inputClass} />
          </label>
          <label className="space-y-2 text-sm font-medium text-ink/70">
            <span>Ville ou région <span className="text-ink/35">(facultatif)</span></span>
            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="ex. Lyon" className={inputClass} />
          </label>
          <label className="space-y-2 text-sm font-medium text-ink/70">
            <span>Nombre de profils</span>
            <input
              type="number"
              min={1}
              max={MAX_RESULTS}
              value={maxResults}
              onChange={(e) => setMaxResults(e.target.value)}
              onBlur={() => setMaxResults((v) => clamp(v))}
              className={inputClass}
            />
          </label>
        </div>

        <p className="text-xs text-ink/40">
          {SEARCH_CREDIT_COST} crédit par recherche, puis {PROSPECT_CREDIT_COST} crédits par prospect ajouté au CRM (un profil
          déjà dans le CRM n'est pas facturé). Maximum {MAX_RESULTS} profils par recherche ; un plafond par jour protège
          votre compte LinkedIn.
        </p>

        {job && job.kind === 'linkedin.search' && job.status !== 'completed' && <SearchJobStatus job={job} />}

        <div className="flex justify-end border-t border-ink/10 pt-5">
          <button
            type="submit"
            disabled={search.isPending || ['queued', 'running'].includes(job?.status)}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brand-strong disabled:cursor-not-allowed disabled:opacity-60"
          >
            {search.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Lancer la recherche
          </button>
        </div>
      </form>

      {job?.kind === 'linkedin.search' && job.status === 'completed' && <Results job={job} />}
    </>
  )
}

function Results({ job }) {
  const { importProfiles } = useLinkedinSearch()
  const profiles = useMemo(() => job.result?.profiles ?? [], [job])
  const [selected, setSelected] = useState(() => new Set(profiles.map((p) => p.linkedinUrl)))
  const [added, setAdded] = useState(null)

  useEffect(() => {
    setSelected(new Set(profiles.map((p) => p.linkedinUrl)))
    setAdded(null)
  }, [profiles])

  const toggle = (url) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(url)) next.delete(url)
      else next.add(url)
      return next
    })
  const allSelected = selected.size === profiles.length && profiles.length > 0

  const handleImport = () => {
    importProfiles.mutate(
      { jobId: job.id, profileUrls: [...selected] },
      {
        onSuccess: (data) => {
          setAdded(data)
          const parts = [`${data.created} prospect${data.created > 1 ? 's' : ''} ajouté${data.created > 1 ? 's' : ''} au CRM`]
          if (data.alreadyInCrm > 0) parts.push(`${data.alreadyInCrm} déjà présent${data.alreadyInCrm > 1 ? 's' : ''}`)
          toast.success(parts.join(', '))
        },
        onError: (error) => {
          if (error?.response?.status === 402) {
            toast.error(`Crédits insuffisants : ${error.response.data?.required ?? '?'} crédits nécessaires`)
          } else {
            toast.error(errorMessage(error, 'Ajout impossible'))
          }
        },
      }
    )
  }

  if (profiles.length === 0) {
    return (
      <div className="rounded-[14px] border border-ink/10 bg-ink/[0.03] p-6 text-sm text-ink/60">
        Aucun profil trouvé pour cette recherche. Essayez un intitulé de poste plus courant ou une zone plus large.
      </div>
    )
  }

  return (
    <div className="space-y-4 rounded-[14px] border border-ink/10 bg-ink/[0.03] p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="m-0 text-lg font-bold text-ink">
            {profiles.length} profil{profiles.length > 1 ? 's' : ''} trouvé{profiles.length > 1 ? 's' : ''}
          </h3>
          {job.result?.limitReached && (
            <p className="m-0 mt-1 text-xs text-amber-300">LinkedIn a signalé la limite mensuelle de recherches de ce compte.</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setSelected(allSelected ? new Set() : new Set(profiles.map((p) => p.linkedinUrl)))}
          className="text-sm text-ink/60 hover:text-ink"
        >
          {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
        </button>
      </div>

      <ul className="m-0 list-none divide-y divide-ink/5 p-0">
        {profiles.map((profile) => {
          const checked = selected.has(profile.linkedinUrl)
          const Box = checked ? CheckSquare : Square
          return (
            <li key={profile.linkedinUrl} className="flex items-start gap-3 py-3">
              <button
                type="button"
                onClick={() => toggle(profile.linkedinUrl)}
                className={`mt-0.5 shrink-0 ${checked ? 'text-brand-text' : 'text-ink/30'}`}
                aria-label={checked ? 'Ne pas ajouter ce profil' : 'Ajouter ce profil'}
              >
                <Box className="h-5 w-5" />
              </button>
              <div className="min-w-0 flex-1">
                <p className="m-0 text-sm font-semibold text-ink">{profile.fullName || 'Membre LinkedIn'}</p>
                <p className="m-0 text-sm text-ink/60">
                  {[profile.jobTitle, profile.companyName].filter(Boolean).join(' · ') || profile.headline || '—'}
                </p>
                {profile.location && <p className="m-0 text-xs text-ink/40">{profile.location}</p>}
              </div>
              <a
                href={profile.linkedinUrl}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 text-ink/40 hover:text-[#4a9be8]"
                aria-label="Voir le profil LinkedIn"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            </li>
          )
        })}
      </ul>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ink/10 pt-4">
        {added ? (
          <Link to="/dashboard/clients/prospects" className="text-sm font-semibold text-brand-text hover:underline">
            Voir mes prospects dans le CRM →
          </Link>
        ) : (
          <span className="text-xs text-ink/40">
            Jusqu'à {selected.size * PROSPECT_CREDIT_COST} crédits ({PROSPECT_CREDIT_COST} par nouveau prospect)
          </span>
        )}
        <button
          type="button"
          onClick={handleImport}
          disabled={selected.size === 0 || importProfiles.isPending}
          className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brand-strong disabled:cursor-not-allowed disabled:opacity-50"
        >
          {importProfiles.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
          Ajouter {selected.size} au CRM
        </button>
      </div>
    </div>
  )
}
